import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useFetchInterviewPacks from "../hooks/useFetchInterviewPacks";
import useCreatePack from "../hooks/useCreatePack";
import useDeletePack from "../hooks/useDeletePack";
import "./AdminDomainPacksScreen.css";

export default function AdminDomainPacksScreen() {

    const { domainId } = useParams(); // ✅ domain ID from route
    const { packList, loadingPackList } = useFetchInterviewPacks(domainId);
    const { createPack, creatingPack, creatingPackError } = useCreatePack();
    const { deletePack } = useDeletePack();
    const navigate = useNavigate();

    // 🔸 Local state for "Add Pack" popup
    const [isAddPackVisible, setIsAddPackVisible] = useState(false);
    const [newPackName, setNewPackName] = useState("");
    const [newPackOrder, setNewPackOrder] = useState("");
    const [newPackMRP, setNewPackMRP] = useState("");
    const [newPackPrice, setNewPackPrice] = useState("");
    const [newPackCoupon, setNewPackCoupon] = useState("");
    const [newPackValidity, setNewPackValidity] = useState("");

    // ✅ Create new pack
    const handleCreatePack = async () => {
        if (!newPackName.trim()) return alert("Please enter pack name");
        if (!newPackOrder.trim() || isNaN(newPackOrder))
            return alert("Please enter a valid numeric order ID");
        if (!newPackMRP.trim() || isNaN(newPackMRP))
            return alert("Please enter a valid numeric MRP");
        if (!newPackPrice.trim() || isNaN(newPackPrice))
            return alert("Please enter a valid numeric price");
        if (!newPackValidity.trim() || isNaN(newPackValidity))
            return alert("Please enter validity (in days)");
        if (!newPackCoupon.trim()) return alert("Please enter a coupon code");

        const orderValue = parseInt(newPackOrder, 10);

        try {
            const success = await createPack(
                newPackName,
                newPackMRP,
                newPackPrice,
                newPackCoupon,
                newPackValidity,
                domainId,
                null,
                orderValue
            );

            if (success) {
                alert("✅ Pack created successfully!");
                // Reset form and close modal
                setIsAddPackVisible(false);
                setNewPackName("");
                setNewPackOrder("");
                setNewPackMRP("");
                setNewPackPrice("");
                setNewPackCoupon("");
                setNewPackValidity("");
            } else {
                alert("❌ Failed to create pack. Try again.");
            }
        } catch (error) {
            console.error(error);
            alert("❌ Something went wrong. Try again.");
        }
    };

    // ✅ Navigate to Pack details
    const handleViewQuestionCategory = () => {
        navigate(`/admin/domain/${domainId}/QuestionCategories`);
    };

    // ✅ Navigate to Create Question Screen
    const handleCreateQuestion = () => {
        navigate(`/admin/domain/${domainId}/all-questions`);
    };

    // ✅ Navigate to Pack details
    const handlePackClick = (packId) => {
        navigate(`/admin/domain/${domainId}/pack/${packId}/interviews`);
    };

    // ✅ Delete Pack
    const handlePackDelete = async (packId) => {
        if (!window.confirm("Are you sure you want to delete this pack?")) return;
        try {
            await deletePack(domainId, packId);
        } catch (error) {
            console.error(error);
            alert("❌ Failed to delete pack. Try again.");
        }
    };

    return (
        <div className="domain-home-page">
            <h2 className="domain-title">Interview Packs</h2>

            {/* Top Action Buttons */}
            <div className="domain-actions-top">
                <button
                    className="create-pack-btn"
                    onClick={() => setIsAddPackVisible(true)}
                >
                    ➕ Create Pack
                </button>
                <button
                    className="view-all-question-btn"
                    onClick={handleViewQuestionCategory}
                >
                     View Question Categories
                </button>
                <button
                    className="view-all-question-btn"
                    onClick={handleCreateQuestion}
                >
                    📝 View All Questions
                </button>
            </div>

            {/* List Section */}
            <div className="pack-list-section">
                {loadingPackList ? (
                    <p className="info-text">Loading packs...</p>
                ) : packList.length === 0 ? (
                    <p className="info-text italic">No packs found.</p>
                ) : (
                    <ul className="pack-list">
                        {packList.map((pack) => (
                            <li
                                key={pack.id}
                                className="pack-item"
                                onClick={() => handlePackClick(pack.id)}
                            >
                                <div className="pack-info">
                                    <div>
                                        <p className="pack-name">{pack.name}</p>
                                        <p className="pack-order">Order: {pack.order}</p>
                                    </div>
                                    <button
                                        className="delete-pack-btn"
                                        onClick={(e) => {
                                            e.stopPropagation(); // prevent triggering handlePackClick
                                            handlePackDelete(pack.id);
                                        }}
                                    >
                                        🗑️ Delete
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* 🔹 Popup for Adding Pack */}
            {isAddPackVisible && (
                <div className="overlay" onClick={() => setIsAddPackVisible(false)}>
                    <div
                        className="add-pack-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3>Add New Pack</h3>

                        {/* Pack Name */}
                        <label className="modal-label">Pack Name</label>
                        <input
                            type="text"
                            className="modal-input"
                            value={newPackName}
                            onChange={(e) => setNewPackName(e.target.value)}
                        />

                        {/* Order ID */}
                        <label className="modal-label">Order ID</label>
                        <input
                            type="number"
                            className="modal-input"
                            value={newPackOrder}
                            onChange={(e) => setNewPackOrder(e.target.value)}
                        />

                        {/* MRP */}
                        <label className="modal-label">MRP</label>
                        <input
                            type="number"
                            className="modal-input"
                            value={newPackMRP}
                            onChange={(e) => setNewPackMRP(e.target.value)}
                        />

                        {/* Price */}
                        <label className="modal-label">Price</label>
                        <input
                            type="number"
                            className="modal-input"
                            value={newPackPrice}
                            onChange={(e) => setNewPackPrice(e.target.value)}
                        />

                        {/* Validity */}
                        <label className="modal-label">Validity (Days After Purchase)</label>
                        <input
                            type="number"
                            className="modal-input"
                            value={newPackValidity}
                            onChange={(e) => setNewPackValidity(e.target.value)}
                        />

                        {/* Coupon */}
                        <label className="modal-label">Coupon Code</label>
                        <input
                            type="text"
                            className="modal-input"
                            value={newPackCoupon}
                            onChange={(e) => setNewPackCoupon(e.target.value)}
                        />

                        {creatingPackError && (
                            <p className="error-text">{creatingPackError}</p>
                        )}

                        <button
                            className="modal-submit-btn"
                            onClick={handleCreatePack}
                            disabled={creatingPack}
                        >
                            {creatingPack ? "Creating..." : "Create Pack"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
